import React, { useRef, useState } from 'react'
import { useClaimDocumentUpload } from '@/services/healthClaim/useClaimDocumentUpload'
import { DOCUMENT_TYPE, MAX_CLAIM_DOCUMENT_SIZE_BYTES } from '@/constant/E_CLAIM'
import { DocumentAction, UploadStatus } from '../types'
import message from '@/libs/message'
import { useParams } from 'react-router-dom'
import { useClaimDocumentDownload } from '@/services/healthClaim/useClaimDocumentDownload'
import { ClaimDocumentResponse } from '@/services/healthClaim/useGetClaimDetail/types'
import { useHealthClaimForm } from '@/contexts/healthClaimForm'

const DocumentsConsiderationHook = ({
  code,
  dispatch,
  claimDocuments,
}: {
  code: DOCUMENT_TYPE
  dispatch: React.Dispatch<DocumentAction>
  claimDocuments: ClaimDocumentResponse[]
}) => {
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('none')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const params = useParams()
  const { claimFormId } = params
  const { markEditedOnce } = useHealthClaimForm()

  const { mutateAsync: downloadAll, isPending } = useClaimDocumentDownload()

  // Mutation hook for uploading claim documents
  const { mutateAsync: uploadDocument } = useClaimDocumentUpload(code, setUploadProgress)

  const triggerFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    fileInputRef.current?.click()
  }

  const handleDownloadClick = async () => {
    if (!claimFormId) return
    if (!claimDocuments && claimDocuments.length > 0) return
    if (isPending) return

    try {
      const { blob, filename } = await downloadAll({
        claimId: claimFormId,
        documentTypeId: claimDocuments[0].documentType.id,
      })
      // สร้างลิงก์ดาวน์โหลดจาก Blob
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (error: any) {
      message.error(error?.message || 'ดาวน์โหลดไม่สำเร็จ')
    }
  }

  const handleRemoveSelectFile = () => {
    setSelectedFile(null)
  }

  const handleRemoveClaimDocument = (id: string) => {
    dispatch({ type: 'REMOVE_CLAIMED', code, id })
    markEditedOnce()
    message.success('ลบรูปภาพสำเร็จ').then(undefined)
  }

  const handleRemoveUploadClaimDocument = (id: string) => {
    dispatch({ type: 'REMOVE_UPLOADED', code, id })
    markEditedOnce()
    message.success('ลบรูปภาพสำเร็จ').then(undefined)
  }

  const handleRemovePreview = (id: string, code: string, type: 'UPLOADED' | 'SUCCESS') => {
    if (type === 'SUCCESS') {
      dispatch({ type: 'REMOVE_CLAIMED', code, id })
    }
    if (type === 'UPLOADED') {
      dispatch({ type: 'REMOVE_UPLOADED', code, id })
    }
    markEditedOnce()
    message.success('ลบรูปภาพสำเร็จ').then(undefined)
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (file.size > MAX_CLAIM_DOCUMENT_SIZE_BYTES) {
        message.error('ไฟล์มีขนาดเกิน 500MB กรุณาเลือกไฟล์ที่มีขนาดเล็กกว่า', 10)
        return
      }
      await uploadFile(file)
    }
  }

  const uploadFile = async (file: File) => {
    try {
      setSelectedFile(file)
      setUploadProgress(0)
      setUploadStatus('uploading')

      const response = await uploadDocument(file)

      dispatch({
        type: 'SET_UPLOADING',
        code,
        payload: { ...response, mimeType: response.mimeType ?? file.type },
      })
      // mark edited on successful upload
      markEditedOnce()

      handleRemoveSelectFile()
      setUploadStatus('none')
    } catch (error) {
      setUploadStatus('retry')
    }
  }

  return {
    selectedFile,
    fileInputRef,
    uploadStatus,
    uploadProgress,
    triggerFilePicker,
    handleRemoveUploadClaimDocument,
    handleRemoveClaimDocument,
    handleRemoveSelectFile,
    handleDownloadClick,
    handleFileChange,
    uploadFile,
    handleRemovePreview,
  }
}

export default DocumentsConsiderationHook
